import { fetchApi } from "@tiny/utils";
import React, { useState, useEffect } from "react";
import { GetSenderDetailsResponse } from "@/types/api/senderDetails";
import { useNavigate, useRouteLoaderData } from "react-router-dom";
import { RootLoader } from "@/types/RootLoader";
import {
  AuthStatus,
  AuthStatusReponse,
  DomainOption,
} from "./DNSAuthentication.types";

const useDnsAuthentication = () => {
  const [domains, setDomains] = useState<DomainOption[]>([]);
  const [senderDetailsData, setSenderDetailsData] = useState<
    GetSenderDetailsResponse[]
  >([]);
  const [authStatus, setAuthStatus] = React.useState<AuthStatus>({
    dkim: false,
    dmarc: false,
    spf: false,
  });
  const [authError, setAuthError] = React.useState<string>();
  const { accountInfo } = useRouteLoaderData("root") as RootLoader;

  const navigate = useNavigate();

  const fetchDKIMDomains = async () => {
    const data = await fetchApi<GetSenderDetailsResponse[]>(
      `/accountservice/account/${accountInfo?.id}/sender-details`
    );

    setSenderDetailsData(data);

    const transformedData = data.reduce((acc, item) => {
      const domain = item.email.split("@")[1] || "";
      const existingDomain = acc.find((d) => d.domain === domain);
      if (!existingDomain) {
        acc.push({
          domain,
          enabled: false,
        });
      }
      return acc;
    }, [] as DomainOption[]);
    setDomains(transformedData);
  };

  const fetchAuthenticationStatus = async (domain: string) => {
    setAuthStatus({ dkim: false, dmarc: false, spf: false });
    setAuthError("");

    if (domain) {
      try {
        const senderForDomain = senderDetailsData.find(
          (sender) => sender.email.split("@")[1] === domain
        );

        if (!senderForDomain) {
          setAuthError("No email found for this domain");
          return;
        }

        const data = await fetchApi<AuthStatusReponse>(
          `/notificationservice/v0/email-auth/validate?email=${senderForDomain.email}`,
          {
            method: "GET",
          }
        );

        const newAuthStatus = {
          dkim: data.dkimSigned,
          dmarc: data.dmarcSigned,
          spf: data.spfSigned,
        };

        setAuthStatus(newAuthStatus);
        setAuthError("");
      } catch (error: any) {
        setAuthError(
          "DNS settings are not properly set up with your domain registrar. To learn how to set up, Visit here."
        );
      }
    }
  };

  useEffect(() => {
    fetchDKIMDomains();
  }, []);

  const navigateToDNSConfig = () => {
    navigate("/main/account-settings/sender-details");
  };

  return {
    domains,
    fetchAuthenticationStatus,
    authStatus,
    authError,
    navigateToDNSConfig,
  };
};

export default useDnsAuthentication;
